<template>
  <div id="app">
    <Hero />
    <SectionIntro />
    <StepOne />
    <StepTwo>
      <div class="form">
        <form class="form__group" @submit.prevent="loadDiscordGuilds()">
          <input
            v-model="discordToken"
            id="discord-token"
            aria-label="Paste your Discord token here"
            type="password"
            autocomplete="off"
            placeholder="Your Discord token (e.g. mfa.-u9TUN16gHLC-ZEkv6Og-f_uNHF_FErZ85agt8Oj9K9Vi6LfCEHAxUxUrHWLZiXMa4udSix2oxn9aQJOmeqO)"
            class="form__item form__input api-input"
          />
          <label for="discord-token" class="visually-hidden">Paste your 90-letters-long Discord token here</label>
          <button
            :disabled="!tokenIsPresent()"
            type="submit"
            @click="loadDiscordGuilds()"
            class="btn btn--primary form__item"
            :class="{'is-disabled': !tokenIsPresent()}"
          >
            Get raw data from Discord
          </button>
        </form>

        <div class="form__group big-offset form__hidden-block" :class="{'is-visible': getSelectedGuild()}">
          <template v-if="getSelectedGuild()">
            <div class="form__item form__inline-item">
              <label for="discord-guild-selector" class="form__item form__label">Select your Discord guild</label>
              <select
                :v-model="discordGuildSelectedId"
                id="discord-guild-selector"
                class="form__item form__input"
              >
                <option
                  v-for="guild in discordGuildsList"
                  :value="guild.id"
                  :key="guild.id"
                >
                  {{guild.label}}
                </option>
              </select>
            </div>
            <button
              :disabled="!getSelectedGuild()"
              @click="exportDiscordChannels()"
              class="btn btn--primary form__item"
              :class="{'is-disabled': !getSelectedGuild()}"
            >
              Download export file
            </button>
          </template>
        </div>

        <div class="form__group form__messages" role="status" aria-live="polite">
          <template v-if="dataIsLoading">
            <p class="form__message-item">Loading data...</p>
          </template>

          <template v-if="errorMessage">
            <p class="form__message-item" style="color: red">{{errorMessage}}</p>
          </template>

          <div v-if="channelsListArchiveIsReady || messagesArchiveIsReady" class="visually-hidden">
            <p class="form__message-item">Save your file</p>
          </div>
        </div>
      </div>
    </StepTwo>
    <StepThree :disabled="!getSelectedGuild()" />
    <SectionCongrats />
    <StepFour />
    <StepFive :disabled="!getSelectedGuild()">
      <template>
        Simply use the same native Slack importing tool as in <a class="not-decorated" href="#step-three"><code>Step 3</code></a> to import <strong><a v-on:click.prevent="exportDiscordMessages()" href="#" :class="{'is-disabled': !getSelectedGuild()}">your Discord data</a></strong> again.
        <br>
        Don't worry, it will <strong>not</strong> re-write the existing data. Karma bot will match your former Discord users and channels with the freshly created Slack team data.
      </template>
    </StepFive>
    <SectionOutro />
    <Footer />
  </div>
</template>

<script>
  import Discord from 'discord.js';
  import JSZip from 'jszip';
  import saveAs from 'file-saver';
  import {transliterate as tr, slugify} from 'transliteration';

  import Hero from './components/Hero'
  import SectionIntro from './components/SectionIntro'
  import StepOne from './components/StepOne'
  import StepTwo from './components/StepTwo'
  import StepThree from './components/StepThree'
  import SectionCongrats from './components/SectionCongrats'
  import StepFour from './components/StepFour'
  import StepFive from './components/StepFive'
  import SectionOutro from './components/SectionOutro'
  import Footer from './components/Footer'

  export default {
    name: 'app',
    components: {
      Hero,
      SectionIntro,
      StepOne,
      StepTwo,
      StepThree,
      SectionCongrats,
      StepFour,
      StepFive,
      SectionOutro,
      Footer,
    },
    data: function() {
      return {
        discordClient: null,
        discordToken: '',
        discordGuilds: null,
        discordGuildSelectedId: '',
        errorMessage: '',
        dataIsLoading: false,
        channelsListArchiveIsReady: false,
        messagesArchiveIsReady: false,
      }
    },
    computed: {
      discordGuildsList() {
        if (this.discordGuilds) {
          return this.discordGuilds.map(guild => ({
            id: guild.id,
            label: guild.name,
          }));
        } else {
          return [];
        }
      }
    },
    methods: {
      tokenIsPresent() {
        return !!this.discordToken.length
      },
      getSelectedGuild() {
        return this.discordGuildSelectedId;
      },
      loadDiscordGuilds() {
        // reset previous state in case of re-connecting or changing token
        this.errorMessage = '';
        this.discordGuilds = null;
        this.discordGuildSelectedId = '';
        this.dataIsLoading = true;
        this.channelsListArchiveIsReady = false;

        // let't get our user data via its token
        this.discordClient = new Discord.Client();
        const client = this.discordClient;

        client.on('ready', () => {
          this.dataIsLoading = false;

          // guild is a board analog in Discord
          // and our logged in Discord user is a member of these guilds:
          const userGuilds = Array.from(client.guilds.values());
          // And now we should ask user what guild he wants to import
          this.discordGuilds = userGuilds;

          if (!userGuilds.length) {
            this.errorMessage = 'No Discord guilds found!';
          } else {
            this.discordGuildSelectedId = userGuilds[0].id;
          }
        });

        client.on('error', (error) => {
          this.dataIsLoading = false;
          this.errorMessage = error.message;
        });

        client.login(this.discordToken)
              .catch(error => {
                this.dataIsLoading = false;
                this.errorMessage = error.message;
              });
      },
      exportDiscordChannels() {
        const rootComponent = this;
        const selectedGuild = this.discordGuilds.find(guild => guild.id === this.discordGuildSelectedId);
        const membersInfo = [];

        rootComponent.dataIsLoading = true;

        if (selectedGuild.available && selectedGuild.memberCount) {
          // let's record all users to json file
          selectedGuild.members.forEach(member => {
            const avatarUrl = member.user.displayAvatarURL.split('?')[0];

            if (!member.user.bot) {
              membersInfo.push({
                id: `member_${member.user.id}`,
                team_id: `team_${selectedGuild.id}`,
                name: member.user.username,
                deleted: member.deleted,
                color: member.displayHexColor.substring(1),
                real_name: member.user.username,
                tz: '',
                tz_label: '',
                tz_offset: 0,
                profile: {
                  title: '',
                  phone: '',
                  skype: '',
                  real_name: member.user.username,
                  real_name_normalized: member.user.username,
                  display_name: member.user.username,
                  display_name_normalized: member.user.username,
                  fields: null,
                  status_text: '',
                  status_emoji: '',
                  status_expiration: 0,
                  avatar_hash: member.user.avatar,
                  image_24: `${avatarUrl}?size=32`,
                  image_32: `${avatarUrl}?size=32`,
                  image_48: `${avatarUrl}?size=64`,
                  image_72: `${avatarUrl}?size=128`,
                  image_192: `${avatarUrl}?size=256`,
                  image_512: `${avatarUrl}?size=512`,
                  status_text_canonical: '',
                  team: `team_${selectedGuild.id}`,
                },
                is_admin: false,
                is_owner: false,
                is_primary_owner: false,
                is_restricted: false,
                is_ultra_restricted: false,
                is_bot: false,
                is_app_user: false,
                updated: '',
              })
            }
          });

          // let's loop through guild text channels
          const selectedGuildChannels = [];

          selectedGuild.channels.forEach(channel => {
            if (channel.type === 'text') {
              selectedGuildChannels.push(channel)
            }
          });

          if (selectedGuildChannels.length) {
            // create json file contains all the channels info

            const channelsArchive = new JSZip();
            const space = '\t';

            const channelsInfo = [];

            selectedGuildChannels.forEach(discordChannel => {
              // slack import/export tool does not work properly with non-english-named channels
              const channelNameTr = tr(discordChannel.name);
              const channelNameSl = slugify(channelNameTr, {separator: '_'});
              const channelName = channelNameSl.slice(0, 20);
              const channelMembers = [];

              discordChannel.members.forEach(member => {
                channelMembers.push(`member_${member.user.id}`)
              });

              channelsArchive.folder(channelName);

              channelsInfo.push({
                id: `channel_${discordChannel.id}`,
                name: channelName,
                created: Math.round(+new Date(discordChannel.createdTimestamp) / 1000).toString(),
                creator: `member_${discordChannel.client.user.id}`,
                is_archived: false,
                is_general: channelName === 'general',
                members: channelMembers,
                topic: {
                  value: discordChannel.topic,
                  creator: '',
                  last_set: discordChannel.topic ? Math.round(+new Date() / 1000).toString() : '',
                },
                purpose: {
                  value: '',
                  creator: '',
                  last_set: '',
                }
              });
            });

            channelsArchive.file('channels.json', JSON.stringify(channelsInfo, null, space));
            channelsArchive.file('users.json', JSON.stringify(membersInfo, null, space));

            channelsArchive.generateAsync({type: 'blob'})
              .then(content => {
                rootComponent.channelsListArchiveIsReady = true;
                rootComponent.dataIsLoading = false;
                saveAs(content, `discord_${selectedGuild.name}_guild_channels_archive.zip`);
              });
          } else {
            this.dataIsLoading = false;
            this.errorMessage = 'Sorry, cannot find any channels in selected guild'
          }
        } else {
          this.dataIsLoading = false;
          this.errorMessage = 'Sorry, your guild is not available or has no members in it'
        }
      },
      exportDiscordMessages() {
        const rootComponent = this;
        const selectedGuild = this.discordGuilds.find(guild => guild.id === this.discordGuildSelectedId);
        const membersInfo = [];

        rootComponent.dataIsLoading = true;

        if (selectedGuild.available && selectedGuild.memberCount) {
          // let's record all users to json file
          selectedGuild.members.forEach(member => {
            const avatarUrl = member.user.displayAvatarURL.split('?')[0];

            membersInfo.push({
              id: `member_${member.user.id}`,
              team_id: `team_${selectedGuild.id}`,
              name: member.user.username,
              deleted: member.deleted,
              color: member.displayHexColor.substring(1),
              real_name: member.user.username,
              tz: '',
              tz_label: '',
              tz_offset: 0,
              profile: {
                title: '',
                phone: '',
                skype: '',
                real_name: member.user.username,
                real_name_normalized: member.user.username,
                display_name: member.user.username,
                display_name_normalized: member.user.username,
                fields: null,
                status_text: '',
                status_emoji: '',
                status_expiration: 0,
                avatar_hash: member.user.avatar,
                image_24: `${avatarUrl}?size=32`,
                image_32: `${avatarUrl}?size=32`,
                image_48: `${avatarUrl}?size=64`,
                image_72: `${avatarUrl}?size=128`,
                image_192: `${avatarUrl}?size=256`,
                image_512: `${avatarUrl}?size=512`,
                status_text_canonical: '',
                team: `team_${selectedGuild.id}`,
              },
              is_admin: false,
              is_owner: false,
              is_primary_owner: false,
              is_restricted: false,
              is_ultra_restricted: false,
              is_bot: member.user.bot,
              is_app_user: false,
              updated: '',
            })
          });

          // let's loop through guild text channels
          const selectedGuildChannels = [];

          selectedGuild.channels.forEach(channel => {
            if (channel.type === 'text') {
              selectedGuildChannels.push(channel)
            }
          });

          if (selectedGuildChannels.length) {
            // create json file contains all the channels info

            const channelsInfo = [];

            selectedGuildChannels.forEach(discordChannel => {
              // slack import/export tool does not work properly with non-english-named channels
              const channelNameTr = tr(discordChannel.name);
              const channelNameSl = slugify(channelNameTr, {separator: '_'});
              const channelName = channelNameSl.slice(0, 20);
              const channelMembers = [];

              discordChannel.members.forEach(member => {
                channelMembers.push(`member_${member.user.id}`)
              });

              channelsInfo.push({
                id: `channel_${discordChannel.id}`,
                name: channelName,
                created: Math.round(+new Date(discordChannel.createdTimestamp) / 1000).toString(),
                creator: `member_${discordChannel.client.user.id}`,
                is_archived: false,
                is_general: channelName === 'general',
                members: channelMembers,
                topic: {
                  value: discordChannel.topic,
                  creator: '',
                  last_set: discordChannel.topic ? Math.round(+new Date() / 1000).toString() : '',
                },
                purpose: {
                  value: '',
                  creator: '',
                  last_set: '',
                }
              });
            });

            // now let's grab all the messages
            const promises = [];
            const channelsMessagesStorage = {};

            selectedGuildChannels.forEach(discordChannel => {
              promises.push(
                discordChannel.fetchMessages()
                  .then(messages => {
                    if (messages.size) {
                      messages.forEach(message => {
                        // somehow split them by dates

                        if (message.type === 'DEFAULT') {
                          const messageChannelNameTr = tr(message.channel.name);
                          const messageChannelNameSl = slugify(messageChannelNameTr, {separator: '_'});
                          const messageChannelName = messageChannelNameSl.slice(0, 20);
                          const messageDate = new Date(message.createdTimestamp);
                          const YYYY = messageDate.getFullYear();
                          const MM = (messageDate.getMonth() + 1).toString().padStart(2, '0');
                          const DD = messageDate.getDate().toString().padStart(2, '0');
                          const messageDateFormatted = `${YYYY}-${MM}-${DD}`;

                          if (!channelsMessagesStorage[messageChannelName]) {
                            channelsMessagesStorage[messageChannelName] = {}
                          }

                          if (!channelsMessagesStorage[messageChannelName][messageDateFormatted]) {
                            channelsMessagesStorage[messageChannelName][messageDateFormatted] = []
                          }

                          const messagesOnDate = channelsMessagesStorage[messageChannelName][messageDateFormatted];

                          messagesOnDate.push({
                            type: 'message',
                            // todo: prevent erasing user with real id with this mocked 'member_'
                            text: message.content.replace(/<@!/gm, '<@member_'),
                            user: `member_${message.author.id}`,
                            ts: Math.round(+new Date(message.createdTimestamp) / 1000).toString() + '.000000',
                          });
                        }
                      })
                    }
                  })
                  .catch((error) => {
                    this.dataIsLoading = false;
                    console.error(error);
                    this.errorMessage = 'Sorry, there was an error reading your Discord messages'
                  })
              )
            });

            Promise.all(promises)
               .then(() => {
                 const slackLikeArchive = new JSZip();
                 const space = '\t';

                 if (Object.keys(channelsMessagesStorage).length) {
                   Object.keys(channelsMessagesStorage).forEach(channel => {
                     const channelDatesWithMessages = Object.keys(channelsMessagesStorage[channel]);

                     channelDatesWithMessages.forEach(date => {
                       const messagesToJson = JSON.stringify(channelsMessagesStorage[channel][date], null, space);

                       slackLikeArchive.folder(channel).file(`${date}.json`, messagesToJson);
                     })
                   });

                   slackLikeArchive.file('channels.json', JSON.stringify(channelsInfo, null, space));
                   slackLikeArchive.file('users.json', JSON.stringify(membersInfo, null, space));
                 }

                 return slackLikeArchive;
               })
               .then(archive => {
                 archive.generateAsync({type: 'blob'})
                   .then(content => {
                     rootComponent.messagesArchiveIsReady = true;
                     rootComponent.dataIsLoading = false;
                     saveAs(content, `discord_${selectedGuild.name}_guild_messages_archive.zip`);
                   });
               })
          } else {
            this.dataIsLoading = false;
            this.errorMessage = 'Sorry, cannot find any channels in selected guild'
          }
        } else {
          this.dataIsLoading = false;
          this.errorMessage = 'Sorry, your guild is not available or has no members in it'
        }
      },
    },
  }
</script>

<style lang="scss">
  @import "./assets/styles/base.sass";
</style>
